import { GeneralModal } from "../GeneralModal/GeneralModal";
import { colorcomb, colorNames } from "./constant";
import {
  useAddMilestoneMutation,
  useUpdateMilestoneDetailsMutation,
} from "../../../../store/apiSlice/newInterfaceApiSlice";
import { useState } from "react";
import { orderTypeForUrl } from "../../page/utils/tableDataElements";
import { InputText, ShowError } from "../InputText/InputText";

/**
 * CreateMilestone Component
 *
 * Modal form for creating or editing a milestone. Supports entering a milestone
 * name, selecting a color, and handles both creating new milestones or updating existing ones.
 * Returns to the milestone list after save.
 *
 * @component
 *
 * @param {Function} closeCreateMilestone - Function to close the create milestone form.
 * @param {Object} milestoneName - Initial milestone data (e.g. `{ name: "", color: "" }` or existing milestone).
 * @param {string} orderType - Determines module context for which milestone is being created (e.g. "shipment").
 *
 *
 */

const NAME_LIMIT = 100;

export const CreateMilestone = ({ closeCreateMilestone, milestoneName, orderType }) => {
  const [updateMilestone, { isLoading: updating }] = useUpdateMilestoneDetailsMutation();
  const [milestoneData, setMilestoneData] = useState({
    ...milestoneName,
    color: milestoneName.color || "processing",
  });
  const [addMilestone, { isLoading: creating }] = useAddMilestoneMutation();
  const [saveError, setSaveError] = useState("");
  const saving = updating || creating;
  const name = milestoneData.name || "";
  const canSave = name.trim().length > 0 && name.length <= NAME_LIMIT;
  const selectedColor = milestoneData.color || "processing";

  const handleInputChange = e => {
    setMilestoneData(state => ({ ...state, [e.target.name]: e.target.value }));
  };

  /**
   * Saves the milestone, retaining the form when the server rejects it.
   * @return {Promise<void>} Completion of the save attempt.
   */
  const handleCreateMilestone = async () => {
    if (saving || !canSave) return;
    setSaveError("");
    const data = { name: name.trim(), color: selectedColor };
    try {
      if (milestoneData.id) {
        await updateMilestone({ id: milestoneData.id, data }).unwrap();
      } else {
        await addMilestone({ ...data, module: orderTypeForUrl[orderType].module }).unwrap();
      }
      closeCreateMilestone();
    } catch (error) {
      setSaveError(
        error.data?.detail ||
          (error.data && typeof error.data === "object"
            ? Object.values(error.data).flat().join(" ")
            : "") ||
          "The milestone could not be saved. Please try again.",
      );
    }
  };

  return (
    <GeneralModal
      titleOfModal={milestoneData.id ? "Edit Milestone" : "Create Milestone"}
      isCancelButttonShow={true}
      btnName={saving ? "Saving..." : milestoneData.id ? "Save" : "Create"}
      isChecked={!saving && canSave}
      handleModalWork={handleCreateMilestone}
      closeModal={closeCreateMilestone}
      widthOfGeneralModal="420px"
      classNameOfModal="milestoneModal"
    >
      {saveError && (
        <p className="formError" role="alert">
          {saveError}
        </p>
      )}
      <InputText
        labelOfInput="Name"
        placeholder="Milestone name"
        setText={handleInputChange}
        text={name}
        heightOfInput="36px"
        widthOfInput="100%"
        inputName="name"
      />
      {name.length > NAME_LIMIT && (
        <ShowError errorText={`Limit reached: Maximum ${NAME_LIMIT} characters allowed`} />
      )}
      <div className="colorPicker">
        <span className="colorLabel">Color</span>
        <div className="allColor">
          {colorNames.map(item => (
            <button
              key={item}
              type="button"
              style={{ backgroundColor: colorcomb[item].color }}
              onClick={() => setMilestoneData(state => ({ ...state, color: item }))}
              aria-label={item}
              aria-pressed={selectedColor === item}
              className={selectedColor === item ? "colorBox selected" : "colorBox"}
            />
          ))}
        </div>
        <p className="colorName">{selectedColor}</p>
      </div>
    </GeneralModal>
  );
};
